// transaction.service.ts
import { inject, Injectable, signal } from '@angular/core';
import { Observable, forkJoin, map, of, tap } from 'rxjs';
import { NewTransaction, PromptTransaction, Transaction, CategoryInfo } from '../../models/transaction.model';
import { UtilityService } from '../utility/utility.service';
import { API_CONFIG } from '../../config/api.config';
import { FilterCriteriaForm } from '../../models/filter.model';
import { GroupsService } from '../groups/groups.service';

// Interface for API response
interface ApiTransaction {
  amount: number;
  description: string;
  category: CategoryInfo;
  date: string;
  id?: string;
  user_id: number | string;
  created_at: string;
  reply?: string;
  type?: 'income' | 'expense' | 'saving';
  tags?: string[];
}

/** Key used to store distinct tags in localStorage. */
const TAGS_STORAGE_KEY = 'fina_distinct_tags';
interface PaginatedResponse<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

/**
 * Service to handle transaction operations.
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  private _utilityService = inject(UtilityService);
  private _groupsService = inject(GroupsService);

  public paginationState = signal({ page: 1, total: 0, total_pages: 1, size: 500 });

  private get baseUrl() {
    return `${API_CONFIG.baseUrl}${API_CONFIG.modules.expenses.base}`;
  }

  /**
   * Helper to map API transaction to frontend model.
   * @param apiTransaction Data from API.
   * @returns {Transaction} Mapped transaction.
   */
  private _mapApiTransactionToTransaction(apiTransaction: ApiTransaction): Transaction {
    return {
      ...apiTransaction,
      category: apiTransaction.category
    };
  }

  /**
   * Extracts tags from a list of transactions and syncs them to localStorage.
   * Merges with existing stored tags to maintain a complete set.
   */
  private _syncTagsToStorage(transactions: Transaction[]): void {
    const stored = this.getDistinctTags();
    const tagSet = new Set<string>(stored);
    transactions.forEach(t => {
      t.tags?.forEach(tag => tagSet.add(tag));
    });
    localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(Array.from(tagSet).sort()));
  }

  /**
   * Merges new tags into the stored set (used after single-transaction responses).
   */
  private _mergeTagsToStorage(tags: string[] | undefined): void {
    if (!tags || tags.length === 0) return;
    const stored = this.getDistinctTags();
    const tagSet = new Set<string>(stored);
    tags.forEach(tag => tagSet.add(tag));
    localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(Array.from(tagSet).sort()));
  }

  /**
   * Retrieves distinct tags from localStorage.
   * @returns {string[]} Sorted list of unique tags.
   */
  getDistinctTags(): string[] {
    try {
      const stored = localStorage.getItem(TAGS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Retrieves all transactions.
   * @returns {Observable<Transaction[]>} List of transactions.
   */
  getTransactions(): Observable<Transaction[]> {
    const params: any = { limit: '500' };
    const groupId = this._groupsService.getSelectedGroupId();
    if (groupId) params['group_id'] = groupId;

    return this._utilityService.request<PaginatedResponse<ApiTransaction>>(
      'GET',
      `${this.baseUrl}${API_CONFIG.modules.expenses.endpoints.list}`,
      undefined,
      params
    ).pipe(
      map(response => response.data.map(apiTransaction =>
        this._mapApiTransactionToTransaction(apiTransaction)
      )),
      tap(transactions => this._syncTagsToStorage(transactions))
    );
  }

  /**
   * Creates a new transaction.
   * @param {NewTransaction} transaction Data for the new transaction.
   * @returns {Observable<Transaction>} The created transaction.
   */
  createTransaction(transaction: NewTransaction): Observable<Transaction> {
    return this._utilityService.request<ApiTransaction>(
      'POST',
      `${this.baseUrl}${API_CONFIG.modules.expenses.endpoints.create}`,
      transaction
    ).pipe(
      map(apiTransaction => this._mapApiTransactionToTransaction(apiTransaction)),
      tap(t => this._mergeTagsToStorage(t.tags))
    );
  }

  /**
   * Creates transactions from a text prompt.
   * @param {PromptTransaction} prompt The prompt body and user info.
   * @returns {Observable<{ transactions: Transaction[], reply: string | null }>}
   */
  createTransactionWithPrompt(prompt: PromptTransaction): Observable<{ transactions: Transaction[], reply: string | null }> {
    return this._utilityService.request<{ transactions: ApiTransaction[], reply: string | null }>(
      'POST',
      `${API_CONFIG.baseUrl}${API_CONFIG.modules.prompts.endpoints.process}`,
      prompt
    ).pipe(
      map(response => {
        const transactions = response.transactions.map(t => this._mapApiTransactionToTransaction(t));
        this._syncTagsToStorage(transactions);
        return { transactions, reply: response.reply };
      })
    );
  }

  /**
   * Filters transactions based on criteria.
   * @param {FilterCriteriaForm} filters Filter criteria.
   * @returns {Observable<Transaction[]>} Filtered transactions.
   */
  filterTransactions(filters: FilterCriteriaForm): Observable<Transaction[]> {
    const params: any = {};

    if (filters.categoryId && filters.categoryId.length > 0) {
      params['categoryId'] = filters.categoryId;
    }

    if (filters.startDate) params['startDate'] = filters.startDate;
    if (filters.endDate) params['endDate'] = filters.endDate;

    if (filters.minAmount) params['minAmount'] = filters.minAmount.toString();
    if (filters.maxAmount) params['maxAmount'] = filters.maxAmount.toString();

    if (filters.limit) {
      params['limit'] = filters.limit.toString();
    } else {
      params['limit'] = '500';
    }

    if (filters.page) {
      const limit = filters.limit || 500;
      params['skip'] = ((filters.page - 1) * limit).toString();
    }

    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => {
        if (!params['tags']) params['tags'] = [];
        params['tags'].push(tag);
      });
    }

    if (filters.type) params['type'] = filters.type;

    const groupId = this._groupsService.getSelectedGroupId();
    if (groupId) params['group_id'] = groupId;

    return this._utilityService.request<PaginatedResponse<ApiTransaction>>(
      'GET',
      `${this.baseUrl}${API_CONFIG.modules.expenses.endpoints.filter}`,
      undefined,
      params
    ).pipe(
      map((response: any) => {
        if (!Array.isArray(response) && 'data' in response) {
          const limit = response.limit || filters.limit || 500;
          const currentPage = Math.floor((response.skip || 0) / limit) + 1;
          const totalPages = Math.ceil((response.total || 0) / limit) || 1;
          this.paginationState.set({
            page: currentPage,
            total: response.total || 0,
            total_pages: totalPages,
            size: limit
          });
        }
        const apiTransactions: ApiTransaction[] = Array.isArray(response) ? response : (response.data || []);
        return apiTransactions.map(apiTransaction =>
          this._mapApiTransactionToTransaction(apiTransaction)
        );
      }),
      tap(transactions => this._syncTagsToStorage(transactions))
    );
  }

  /**
   * Deletes a transaction by ID.
   * @param {string} id Transaction ID.
   * @returns {Observable<{ message: string }>} Success message.
   */
  deleteTransaction(id: string): Observable<{ message: string }> {
    return this._utilityService.request<{ message: string }>(
      'DELETE',
      `${this.baseUrl}${API_CONFIG.modules.expenses.endpoints.delete(id)}`
    );
  }

  /**
   * Deletes multiple transactions by IDs.
   * @param {string[]} ids List of transaction IDs.
   * @returns {Observable<{ message: string }>} Success message.
   */
  deleteMultipleTransactions(ids: string[]): Observable<{ message: string }> {
    return this._utilityService.request<{ message: string }>(
      'DELETE',
      `${this.baseUrl}${API_CONFIG.modules.expenses.endpoints.deleteMultiple(ids)}`,
    );
  }

  /**
   * Adds the given tags to every provided transaction, merging with each
   * transaction's current tags without duplicates. Fires one PUT per transaction.
   * @param {Transaction[]} transactions Transactions to tag.
   * @param {string[]} tags Tags to add to each transaction.
   * @returns {Observable<Transaction[]>} The updated transactions.
   */
  addTagsToTransactions(transactions: Transaction[], tags: string[]): Observable<Transaction[]> {
    if (transactions.length === 0 || tags.length === 0) return of([]);
    const requests = transactions.map(t => {
      const merged = Array.from(new Set([...(t.tags ?? []), ...tags]));
      return this.updateTransaction(t.id!, { tags: merged });
    });
    return forkJoin(requests).pipe(tap(updated => this._syncTagsToStorage(updated)));
  }

  /**
   * Updates an existing transaction.
   * @param {string} id Transaction ID.
   * @param {Partial<Transaction>} expense Updated data.
   * @returns {Observable<Transaction>} The updated transaction.
   */
  updateTransaction(id: string, expense: Partial<Transaction>): Observable<Transaction> {
    return this._utilityService.request<ApiTransaction>(
      'PUT',
      `${this.baseUrl}${API_CONFIG.modules.expenses.endpoints.update(id)}`,
      expense
    ).pipe(
      map(apiTransaction => this._mapApiTransactionToTransaction(apiTransaction)),
      tap(t => this._mergeTagsToStorage(t.tags))
    );
  }
}